import { HStack, IconButton, Stack, Text } from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import * as React from "react";
import {
  LuAlignLeft,
  LuCalendar,
  LuEye,
  LuHistory,
  LuSettings2,
} from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteOptimizationRule,
  getOptimizationRules,
} from "../api/optimization-rule";
import { Button } from "../components/button";
import { CreateOptimizationRuleDialog } from "../components/create-optimization-rule-dialog";
import DataTable from "../components/data-table";
import { DeleteButton } from "../components/delete-button";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import { OptimizationRuleDetailsDialog } from "../components/optimization-rule-details-dialog";
import { OptimizationRuleSyncedEventsDialog } from "../components/optimization-rule-synced-events-dialog";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import { useLoading, useToaster } from "../hooks";
import { OptimizationRule } from "../types/optimization-rule";

export default function ConversionEventsPage() {
  const { workspace } = useParams();
  const wrapToast = useToaster();
  const [total, setTotal] = React.useState(0);
  const [rules, setRules] = React.useState<OptimizationRule[]>([]);
  const [isLoadingRules, wrapLoadingRules] = useLoading();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedRule, setSelectedRule] =
    React.useState<OptimizationRule | null>(null);
  const [syncedEventsRule, setSyncedEventsRule] =
    React.useState<OptimizationRule | null>(null);

  const loadRules = React.useCallback(async () => {
    await wrapToast(async () => {
      await wrapLoadingRules(async () => {
        const { total, rules } = await getOptimizationRules(workspace);
        setTotal(total);
        setRules(rules);
      });
    });
  }, [workspace, wrapLoadingRules, wrapToast]);

  React.useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleModalOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      setIsModalOpen(details.open);
      if (!details.open) {
        loadRules();
      }
    },
    [loadRules],
  );

  const handleDetailsModalOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        setSelectedRule(null);
      }
    },
    [],
  );

  const onDeleteRule = React.useCallback(
    async (rule: OptimizationRule) => {
      await wrapToast(async () => {
        await wrapLoadingRules(async () => {
          await deleteOptimizationRule(workspace, rule.id);
          await loadRules();
        });
      });
    },
    [loadRules, workspace, wrapLoadingRules, wrapToast],
  );

  const onViewRule = React.useCallback((rule: OptimizationRule) => {
    setSelectedRule(rule);
  }, []);

  const onViewSyncedEvents = React.useCallback((rule: OptimizationRule) => {
    setSyncedEventsRule(rule);
  }, []);

  const handleSyncedEventsModalOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        setSyncedEventsRule(null);
      }
    },
    [],
  );

  return (
    <Page loading={isLoadingRules}>
      <Stack flex="1">
        <PageHeader
          title="Conversion Events"
          subtitle="Boost ROAS with AI powered conversion events"
        >
          {total > 0 && (
            <Button size="xs" onClick={() => setIsModalOpen(true)}>
              Create Conversion Event
            </Button>
          )}
        </PageHeader>
        {total === 0 ? (
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={<IconCircle Icon={LuSettings2} variant="flat" />}
                title="No Conversion Events"
                description="Add your first conversion event to get started."
              >
                <Button onClick={() => setIsModalOpen(true)}>
                  Create Conversion Event
                </Button>
              </EmptyState>
            </HStack>
          </PageBody>
        ) : (
          <Stack align="top" flex="1" gap="1">
            <DataTable
              data={rules}
              columns={[
                {
                  id: "name",
                  name: "Name",
                  icon: <LuSettings2 />,
                  width: 180,
                  cell: ({ value: rule }) => {
                    return <Text color="fg">{rule.name}</Text>;
                  },
                },
                {
                  id: "description",
                  name: "Description",
                  icon: <LuAlignLeft />,
                  width: 300,
                  cell: ({ value: rule }) => {
                    return <Text>{rule.description || "-"}</Text>;
                  },
                },
                {
                  id: "created-at",
                  name: "Created",
                  icon: <LuCalendar />,
                  width: 180,
                  cell: ({ value: rule }) => {
                    return (
                      <Text>
                        {formatDistanceToNow(rule["created-at"], {
                          addSuffix: true,
                        })}
                      </Text>
                    );
                  },
                },
                {
                  width: 160,
                  cell: ({ value: rule }) => {
                    return (
                      <HStack>
                        <IconButton
                          aria-label="View conversion event"
                          colorPalette="gray"
                          onClick={() => onViewRule(rule)}
                          size="2xs"
                          variant="ghost"
                        >
                          <LuEye />
                        </IconButton>
                        <IconButton
                          aria-label="View synced events"
                          colorPalette="gray"
                          onClick={() => onViewSyncedEvents(rule)}
                          size="2xs"
                          variant="ghost"
                        >
                          <LuHistory />
                        </IconButton>
                        <DeleteButton
                          colorPalette="gray"
                          label="Delete Conversion Event"
                          message="Delete this Conversion Event?"
                          onDelete={() => onDeleteRule(rule)}
                          size="2xs"
                        />
                      </HStack>
                    );
                  },
                },
              ]}
            />
          </Stack>
        )}
      </Stack>

      <CreateOptimizationRuleDialog
        isOpen={isModalOpen}
        onOpenChange={handleModalOpenChange}
      />

      {selectedRule && (
        <OptimizationRuleDetailsDialog
          isOpen={true}
          onOpenChange={handleDetailsModalOpenChange}
          rule={selectedRule}
        />
      )}

      {syncedEventsRule && (
        <OptimizationRuleSyncedEventsDialog
          isOpen={true}
          onOpenChange={handleSyncedEventsModalOpenChange}
          rule={syncedEventsRule}
          workspace={workspace}
        />
      )}
    </Page>
  );
}
